import {
  createContext,
  ReactNode,
  useContext,
  useCallback,
  useState,
} from "react";
import { ChatChannelURLSelectionProvider } from "./useChatChannelURLSelection";
import { ChatChannel } from "../../../../../fay/Api";
import { useChatChannelByRespondentId } from "./Channels";

export const SelectedChatChannelContext =
  createContext<SelectedChatData | null>(null);
export type SelectedChatData = {
  selectedRespondentId: string | undefined;
  selectedChatChannel: ChatChannel | null;
  selectedChatChannelLoading: boolean;
  clearSelectedChatChannel: () => void;
  selectChatChannelByRespondentId: (respondentId: string) => void;
};

// A useSelectedChatChannel hook; persists the selected chat channel
// via a context provider. Returns the selected chat channel and a setter (by ID)
// and selects the chat channel from useApiChatChannels
export const useSelectedChatChannelInternal = () => {
  const [selectedRespondentId, setSelectedRespondentId] = useState<
    string | undefined
  >();

  const {
    data: respondentChatChannel,
    isLoading: respondentChatChannelLoading,
  } = useChatChannelByRespondentId(selectedRespondentId);

  const selectChatChannelByRespondentId = useCallback(
    (respondentId: string) => {
      if (selectedRespondentId === respondentId) {
        return;
      }

      setSelectedRespondentId(respondentId);
      console.log("Selected chatChannel by respondent ID", respondentId);
    },
    [setSelectedRespondentId, selectedRespondentId],
  );

  // Add a function to clear the selected chat channel
  const clearSelectedChatChannel = useCallback(() => {
    setSelectedRespondentId(undefined);
  }, [setSelectedRespondentId]);

  return {
    selectedRespondentId: selectedRespondentId,
    selectedChatChannel: respondentChatChannel ?? null,
    selectChatChannelByRespondentId: selectChatChannelByRespondentId,
    clearSelectedChatChannel,
    selectedChatChannelLoading: respondentChatChannelLoading,
  };
};

export const SelectedChatChannelProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const selectedChatChannelData = useSelectedChatChannelInternal();

  return (
    <SelectedChatChannelContext.Provider value={selectedChatChannelData}>
      <WatchForURLChanges>{children}</WatchForURLChanges>
    </SelectedChatChannelContext.Provider>
  );
};

export const WatchForURLChanges = ({ children }: { children: ReactNode }) => {
  return (
    <ChatChannelURLSelectionProvider>
      {children}
    </ChatChannelURLSelectionProvider>
  );
};

export const useSelectedChatChannel = () => {
  const context = useContext(SelectedChatChannelContext);
  if (!context) {
    throw new Error(
      "useSelectedChatChannel must be used within a SelectedChatChannelProvider",
    );
  }
  return context;
};
